"use client";

import { createContext, useContext, useState, useLayoutEffect, ReactNode, useCallback } from "react";

const STORAGE_KEY = "frenflow-sidebar-collapsed";

interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isHydrated: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
  defaultCollapsed?: boolean;
}

export function SidebarProvider({ children, defaultCollapsed = true }: SidebarProviderProps) {
  // Start with defaultCollapsed to match server render (prevents hydration mismatch)
  const [isCollapsed, setIsCollapsedState] = useState(defaultCollapsed);
  const [isHydrated, setIsHydrated] = useState(false);

  // Read localStorage and correct state synchronously before browser paint
  // useLayoutEffect fires after DOM mutations but BEFORE the browser paints
  // React batches both setState calls into a single synchronous re-render
  useLayoutEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const collapsed = stored !== null ? stored === 'true' : defaultCollapsed;
    setIsCollapsedState(collapsed);
    document.documentElement.setAttribute('data-sidebar-collapsed', String(collapsed));
    // Sync cookie for SSR — next page load will have correct server-rendered content
    document.cookie = `sidebar-collapsed=${collapsed}; path=/; max-age=31536000; SameSite=Lax`;
    setIsHydrated(true);
  }, [defaultCollapsed]);

  // Persist to localStorage, cookie, and sync data attribute when changed
  const setIsCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsedState(collapsed);
    localStorage.setItem(STORAGE_KEY, String(collapsed));
    document.documentElement.setAttribute('data-sidebar-collapsed', String(collapsed));
    document.cookie = `sidebar-collapsed=${collapsed}; path=/; max-age=31536000; SameSite=Lax`;
  }, []);

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, isHydrated }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
